import { Box, Typography, useTheme, alpha } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";

const num = (n) =>
  n != null && n !== "" && !Number.isNaN(Number(n))
    ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : n ?? "—";

/**
 * One backtest trade, for widths where the 7-column ledger grid (628px of minimum
 * column width) cannot fit.
 *
 * Carries every column the grid shows — buy date, buy price, qty, sale date, sale
 * price, P/L and hold days — over four lines, so nothing truncates and there is no
 * horizontal scroll.
 */
export const BTTradeCard = ({ row }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const pl = Number(row.margin);
  const hasPl = !Number.isNaN(pl);
  const plColor = !hasPl
    ? theme.palette.text.secondary
    : pl > 0
      ? theme.palette.profitColor ?? "#16a34a"
      : pl < 0
        ? theme.palette.lossColor ?? "#dc2626"
        : theme.palette.text.secondary;
  const arrow = !hasPl ? "" : pl > 0 ? "▲" : pl < 0 ? "▼" : "•";

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1.25,
        border: `1px solid ${alpha(primary, 0.14)}`,
        bgcolor: theme.palette.background.paper,
        boxShadow: `0 2px 10px ${alpha("#000", 0.04)}`,
        borderLeft: `3px solid ${alpha(plColor, 0.85)}`,
      }}
    >
      {/* Line 1 — the holding window and what it returned */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap" }}>
            {row.buyDate}
          </Typography>
          <ArrowForward sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap" }}>
            {row.saleDate ?? "—"}
            {row.isOpen ? " (open)" : ""}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "0.86rem", fontWeight: 800, color: plColor, whiteSpace: "nowrap" }}>
          {arrow} {num(row.margin)}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", mt: 0.25 }}>
        {row.holdDays != null ? `${row.holdDays} day${Number(row.holdDays) === 1 ? "" : "s"} held` : "—"}
      </Typography>

      {/* Line 2 — entry and exit, side by side */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1,
          mt: 1,
          pt: 1,
          borderTop: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: "0.62rem", color: "text.secondary", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase" }}
          >
            Buy
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
            {num(row.buyPrice)} <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>× {num(row.buyQty)}</Box>
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: "0.62rem", color: "text.secondary", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase" }}
          >
            Sale
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
            {num(row.salePrice)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default BTTradeCard;
